import DisabledTooltip from "@/UI/DisabledTooltip"
import SearchFilter from "@/UI/Filters/SearchFilter"
import { locationsSelector } from "@/atoms/userAtom"
import AddLocationModal from "@/modules/profile/LocationSettings/components/AddLocationModal"
import { PickupDetailsType } from "@/types/createSingleOrder.types"
import { matchField } from "@/utils/compareObjects"
import { faChevronDown, faCirclePlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Select, Divider, Space, Button } from "antd"
import React from "react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"

interface SelectPickupDropdownProps {
  selectedStore: string | null
  handleStore: (id: string) => Promise<void>
  disabled: boolean
  loading: boolean
  isShortDescription?: boolean
  handleClearPickups?: () => Promise<void>
  onClear?: () => void
  isOptional?: boolean
  showEmptyButton?: boolean
}

const SelectPickupDropdown: React.FC<SelectPickupDropdownProps> = ({
  selectedStore,
  handleStore,
  disabled,
  loading,
  handleClearPickups,
  onClear,
  isShortDescription,
  isOptional,
  showEmptyButton = false,
}) => {
  const { t } = useTranslation()
  const locations = useRecoilValue(locationsSelector)
  const [searchValue, setSearchValue] = useState("")
  const [filteredLocations, setFilteredLocations] = useState<PickupDetailsType[]>([])
  useEffect(() => {
    setFilteredLocations(
      locations?.filter((location) => {
        return (
          !searchValue ||
          matchField(location.name, searchValue) ||
          matchField(location?.address, searchValue) ||
          matchField(location?.alias, searchValue) ||
          matchField(location?.postalCode as string, searchValue)
        )
      })
    )
  }, [searchValue, locations])
  const locationOptions = filteredLocations?.map((location) => ({
    value: location.id,
    label: (
      <div className="flex gap-x-2" title={location?.address}>
        {location?.alias ? (
          <p className="font-semibold">{location.alias}</p>
        ) : (
          <p className="font-semibold">{location.name}</p>
        )}
        {!isShortDescription && (
          <p className="text-ellipsis overflow-hidden max-w-[250px]">
            {location.address?.split(",", 2)?.join(",")}
          </p>
        )}
      </div>
    ),
  }))

  return (
    <DisabledTooltip
      enable={disabled}
      label={t("new-orders.DraftOrderTable.pickupSelectDisabled")}
      placement={"bottomLeft"}
    >
      {locations?.length === 0 && showEmptyButton ? (
        <Button>
          <Space style={{ padding: "0 8px 4px" }}>
            <AddLocationModal
              button={
                <div className="flex cursor-pointer">
                  <FontAwesomeIcon
                    className="flex flex-shrink-0 h-3 mx-auto my-auto text-kosmoBlue ml-1 mr-2"
                    icon={faCirclePlus}
                  />
                  <p> {t("profile.AddStoreModal.addNewLocation")}</p>
                </div>
              }
            />
          </Space>
        </Button>
      ) : (
        <Select
          size="middle"
          className="text-sm draft-order-rule-selector"
          onClick={(e) => e.stopPropagation()}
          placeholder={
            isOptional ? (
              <p className="text-base">
                {`${t("new-orders.BatchSelectors.selectPickupStart")} (${t("common.optional")})`}
              </p>
            ) : (
              <p className="text-base">{t("new-orders.BatchSelectors.selectPickupStart")}</p>
            )
          }
          value={selectedStore}
          onChange={handleStore}
          disabled={disabled}
          suffixIcon={
            <FontAwesomeIcon
              size="xs"
              className="text-gray-700"
              icon={faChevronDown}
              color="#6B7280"
            />
          }
          onClear={onClear}
          allowClear={!!onClear}
          loading={loading}
          options={locationOptions}
          getPopupContainer={() => document.getElementById("draft-order-container") as any}
          dropdownRender={(menu) => (
            <>
              <div className="flex items-center space-x-5 px-2.5 py-2">
                <div className="flex flex-grow">
                  <SearchFilter
                    searchValue={searchValue}
                    handleSearchValue={(value) => setSearchValue(value)}
                    size="middle"
                    searchWidth="100%"
                  />
                </div>
                {handleClearPickups && (
                  <p
                    onClick={handleClearPickups}
                    className="cursor-pointer hover:underline hover:text-kosmoPrimaryBlue"
                  >
                    {t("common.clearAll")}
                  </p>
                )}
              </div>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Space style={{ padding: "0 8px 4px" }}>
                <AddLocationModal
                  button={
                    <div className="flex cursor-pointer">
                      <FontAwesomeIcon
                        className="flex flex-shrink-0 h-3 mx-auto my-auto text-kosmoBlue ml-1 mr-2"
                        icon={faCirclePlus}
                      />
                      <p> {t("profile.AddStoreModal.addNewLocation")}</p>
                    </div>
                  }
                />
              </Space>
            </>
          )}
          popupMatchSelectWidth={false}
        />
      )}
    </DisabledTooltip>
  )
}

export default SelectPickupDropdown
